import React, { useState, useEffect } from 'react';
import { Card, Table } from 'react-bootstrap';

const BillingDetails = ({ detail, yearMonth, monthOptions, onMonthChange, isFinalAmount }) => {
    const [expandedType, setExpandedType] = useState('SDK');

    function parseTierDiscountInfo(tierDiscountInfo) {
        try {
            const info = JSON.parse(tierDiscountInfo);
            return info.applied_tiers || [];
        } catch {
            return [];
        }
    }

    // 월 옵션이 year-month 문자열 배열이면 파싱
    const parsedMonthOptions = (monthOptions || []).map(opt => {
        if (typeof opt === 'string') {
            // _final이 있는 경우 처리
            const isFinal = opt.includes('_final');
            const baseYearMonth = isFinal ? opt.replace('_final', '') : opt;
            const [year, month] = baseYearMonth.split('-').map(Number);
            return { year, month, isFinal };
        }
        return opt;
    });

    // 월 변경 핸들러
    const handleMonthChange = (e) => {
        onMonthChange(e.target.value);
    };

    // 서비스별 데이터
    const items = detail?.items || { SDK: [], CF: [], API: [] };

    const calculateTypeTotal = (services) =>
        Array.isArray(services) ? services.reduce((sum, s) => sum + (s.amount || 0), 0) : 0;

    return (
        <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">요금 내역서</h4>
                {yearMonth?.slice(0, 4)}년 {parseInt(yearMonth?.slice(5, 7), 10)}월
                {(() => {
                    // 중간정산 월인지 먼저 체크 (우선순위 1)
                    const isInterimMonth = yearMonth?.includes('_final');

                    if (isInterimMonth) {
                        if (detail?.finalized_at) {
                            // finalized_at에서 날짜 추출하여 기간 표시
                            const dateMatch = detail.finalized_at.match(/(\d{4}-\d{2}-\d{2})/);

                            if (dateMatch) {
                                const finalizedDate = new Date(dateMatch[1]);
                                const endDate = new Date(finalizedDate.getTime() - 24 * 60 * 60 * 1000); // 전날

                                // 해당 월의 1일
                                const year = finalizedDate.getFullYear();
                                const month = finalizedDate.getMonth();
                                const startDate = new Date(year, month, 1);

                                const startDateStr = startDate.toLocaleDateString('ko-KR', {
                                    month: '2-digit',
                                    day: '2-digit'
                                }).replace('.', '월').replace('.', '일').replace(' ', '');

                                const endDateStr = endDate.toLocaleDateString('ko-KR', {
                                    month: '2-digit',
                                    day: '2-digit'
                                }).replace('.', '월').replace('.', '일').replace(' ', '');

                                return ` (${startDateStr} ~ ${endDateStr})`;
                            }
                        }

                        // 중간정산 월이지만 날짜 계산 실패시 기본값
                        const yesterday = new Date(Date.now() - 86400000);
                        const year = yesterday.getFullYear();
                        const month = yesterday.getMonth();
                        const startDate = new Date(year, month, 1);

                        const startDateStr = startDate.toLocaleDateString('ko-KR', {
                            month: '2-digit',
                            day: '2-digit'
                        }).replace('.', '월').replace('.', '일').replace(' ', '');

                        const endDateStr = yesterday.toLocaleDateString('ko-KR', {
                            month: '2-digit',
                            day: '2-digit'
                        }).replace('.', '월').replace('.', '일').replace(' ', '');

                        return ` (${startDateStr} ~ ${endDateStr})`;
                    }

                    // 일반 월인 경우 isFinalAmount로 판단 (우선순위 2)
                    if (isFinalAmount) {
                        return ' (확정금액)';
                    } else {
                        // 기본: 어제 날짜
                        const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('ko-KR', {
                            month: '2-digit',
                            day: '2-digit'
                        }).replace('.', '월').replace('.', '일').replace(' ', '');

                        return ` (미확정금액 ~${yesterday})`;
                    }
                })()}
            </Card.Header>
            <Card.Body className="bg-body-tertiary">
                {Object.keys(items).map(type => (
                    <div key={type} className="mb-3">
                        <div
                            className="d-flex justify-content-between align-items-center cursor-pointer py-2 px-3"
                            style={{
                                backgroundColor: '#f8f9fa',
                                borderRadius: '5px',
                                border: '1px solid #dee2e6'
                            }}
                            onClick={() => setExpandedType(prev => prev === type ? null : type)}
                        >
                            <h6 className="mb-0">{type}</h6>
                            <div className="d-flex align-items-center">
                                <span className="text-muted me-3">
                                    총 금액: ₩{
                                        type === 'SDK' && detail && detail.finalFee !== undefined
                                            ? detail.finalFee.toLocaleString()
                                            : calculateTypeTotal(items[type]).toLocaleString()
                                    }
                                </span>
                                <span>{expandedType === type ? '-' : '+'}</span>
                            </div>
                        </div>
                        {expandedType === type && items[type].length > 0 && (
                            <Table borderless className="fs-10 mt-2">
                                <tbody>
                                    {items[type].map((service, idx) => (
                                        <tr key={idx} className="border-bottom">
                                            <th className="ps-3">{service.name}</th>
                                            <th className="pe-3 text-end">₩{(service.amount || 0).toLocaleString()}</th>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                        {expandedType === type && items[type].length === 0 && type !== 'SDK' && (
                            <p className="fs-10 text-muted mt-2">이용 내역이 없습니다.</p>
                        )}

                        {/* SDK 항목 아래에 detail 정보 표시 */}
                        {type === 'SDK' && expandedType === 'SDK' && detail && (
                            <div className="mt-3">
                                <div className="mb-2">
                                    <strong>에이전트 평균/최대:</strong> {detail.avgAgentCount}건 / {detail.maxAgentCount}건
                                </div>
                                <div className="mb-2">
                                    <strong>Base Price Per Agent:</strong> ₩{detail.basePricePerAgent?.toLocaleString()}
                                </div>
                                <div className="mb-2">
                                    <strong>적용 요금 정책:</strong> {detail.pricingPolicyName}
                                </div>
                                <div className="mb-2">
                                    <strong>구간별 요금 계산</strong>
                                    <Table size="sm" bordered className="mb-0 mt-1">
                                        <thead>
                                            <tr>
                                                <th>구간</th>
                                                <th>에이전트수</th>
                                                <th>단가</th>
                                                <th>구간금액</th>
                                                <th>할인율</th>
                                                <th>구간할인</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parseTierDiscountInfo(detail.tierDiscountInfo).map((tier, idx) => (
                                                <tr key={idx}>
                                                    <td>{tier.tier_level} ({tier.agent_count_from}~{tier.agent_count_to})</td>
                                                    <td>{parseFloat(tier.agents_in_tier).toFixed(4)}</td>
                                                    <td>₩{tier.price_per_agent?.toLocaleString()}</td>
                                                    <td>₩{tier.monthly_fee_for_tier?.toLocaleString()}</td>
                                                    <td>
                                                        {tier.discount_rate}
                                                        {tier.discount_type === 'percent' ? '%' : ''}
                                                    </td>
                                                    <td className="text-success">-₩{tier.tier_discount?.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <th colSpan={5} className="text-end">구간 할인 총액</th>
                                                <th className="text-success">
                                                    -₩{
                                                        Math.abs(
                                                            parseTierDiscountInfo(detail.tierDiscountInfo)
                                                                .reduce((sum, tier) => sum + (tier.tier_discount || 0), 0)
                                                        ).toLocaleString()
                                                    }
                                                </th>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </div>
                                {/* 서버 정책 옵션 및 계산식 */}
                                {(() => {
                                    let optionPrice = 0;
                                    let serverTotal = 0;
                                    if (detail.policyServerCnt && detail.option) {
                                        try {
                                            optionPrice = JSON.parse(detail.option)?.policy?.price || 0;
                                        } catch (e) { console.error(e); }
                                        serverTotal = detail.policyServerCnt * optionPrice;
                                    }
                                    const tierSum = parseTierDiscountInfo(detail.tierDiscountInfo)
                                        .reduce((sum, tier) => sum + (tier.monthly_fee_for_tier || 0), 0);

                                    return (
                                        <div className="mb-2 mt-3">
                                            <div className="d-flex justify-content-between">
                                                <span><strong>구간별 합계</strong></span>
                                                <span>₩{tierSum.toLocaleString()}</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span><strong>서버 정책 합계</strong></span>
                                                <span>
                                                    + ₩{serverTotal.toLocaleString()}
                                                    {serverTotal > 0 && (
                                                        <span className="text-muted ms-2" style={{ fontSize: '0.95em' }}>
                                                            (서버 정책: {detail.policyServerCnt}대 × ₩{optionPrice.toLocaleString()})
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                            <hr className="my-2" />
                                            <div className="d-flex justify-content-between">
                                                <span><strong>최종금액(결제금액)</strong></span>
                                                <span className="fw-bold">₩{detail.finalFee?.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                ))}
            </Card.Body>
            <Card.Footer className="d-flex justify-content-between bg-body-tertiary">
                <div className="fw-semi-bold">Payable Total</div>
                <div className="fw-bold">
                    ₩{detail?.finalFee !== undefined
                        ? detail.finalFee.toLocaleString()
                        : Object.keys(items).reduce(
                            (total, type) => total + calculateTypeTotal(items[type]),
                            0
                        ).toLocaleString()
                    }
                </div>
            </Card.Footer>
        </Card>
    );
};

export default BillingDetails;